import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import { Star, Clock, MapPin, Phone, Award, Shield, Sparkles, Camera, Eye, ArrowRight, Image as ImageIcon, Wine, GlassWater, Music, ChefHat, Martini } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import HeroSection from '../components/HeroSection';
import BookingWidget from '../components/BookingWidget';
import Highlights from '../components/Highlights';
import Testimonials from '../components/Testimonials';
import Footer from '../components/Footer';

interface GalleryItem {
    id: string;
    title: string;
    description: string;
    image: string;
    gallery_category: {
        name: string;
    };
}

const Home = () => {
    const { t, currentLanguage } = useLanguage();
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    // Gallery state
    const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
    const [galleryLoading, setGalleryLoading] = useState(true);

    // Fetch gallery items for preview
    useEffect(() => {
        const fetchGalleryPreview = async () => {
            try {
                const response = await axios.get(`/api/v1/gallery?lang=${currentLanguage}&limit=6`);
                setGalleryItems(response.data || []);
            } catch (error) {
                console.error('Failed to fetch gallery preview:', error);
                setGalleryItems([]);
            } finally {
                setGalleryLoading(false);
            }
        };

        fetchGalleryPreview();
    }, [currentLanguage]);

    const aboutFeatures = [
        {
            icon: Star,
            title: t('home.rating.title'),
            description: t('home.rating.description'),
            color: 'from-accent-400 to-accent-600'
        },
        {
            icon: Clock,
            title: t('home.hours.title'),
            description: t('home.hours.description'),
            color: 'from-primary-400 to-primary-600'
        },
        {
            icon: MapPin,
            title: t('home.location.title'),
            description: t('home.location.description'),
            color: 'from-secondary-400 to-secondary-600'
        }
    ];


    return (
        <div className="pt-16 lg:pt-20 overflow-hidden">
            {/* Hero Section */}
            <HeroSection />

            {/* Booking Widget */}
            <BookingWidget />

            {/* Highlights Section */}
            <Highlights />

            {/* About Preview Section - Enhanced */}
            <section className="relative section-padding bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                {/* Background Decorations */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-20 left-4 w-16 h-16 lg:left-10 lg:w-20 lg:h-20 bg-primary-200 dark:bg-primary-800 rounded-full opacity-20 blur-xl"></div>
                    <div className="absolute bottom-20 right-4 w-24 h-24 lg:right-10 lg:w-32 lg:h-32 bg-accent-200 dark:bg-accent-800 rounded-full opacity-20 blur-xl"></div>
                    <div className="absolute top-1/2 left-1/4 w-12 h-12 lg:w-16 lg:h-16 bg-secondary-200 dark:bg-secondary-600 rounded-full opacity-30 blur-lg"></div>
                </div>

                <div className="container mx-auto max-w-7xl container-padding relative">
                    <motion.div
                        ref={ref}
                        initial={{ opacity: 0, y: 50 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-16"
                    >
                        <div className="inline-flex items-center space-x-2 bg-primary-100 dark:bg-primary-900/30 px-4 py-2 rounded-full mb-6">
                            <Sparkles className="w-5 h-5 text-primary-600" />
                            <span className="text-primary-600 dark:text-primary-400 font-medium text-sm uppercase tracking-wide">
                                {t('home.premiumExperience')}
                            </span>
                        </div>

                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 leading-tight">
                            {t('home.welcome')}{' '}
                            <span className="text-gradient relative">
                                restaurant
                                <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-primary-400 to-accent-400 rounded-full opacity-60"></div>
                            </span>
                        </h2>

                        <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-10 leading-relaxed max-w-3xl mx-auto font-light">
                            {t('home.description')}
                        </p>

                        {/* Trust Indicators */}
                        <div className="flex justify-center items-center space-x-8 mb-16 flex-wrap gap-4">
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-md">
                                <Award className="w-5 h-5 text-accent-600" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('home.badges.entertainmentHub')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-md">
                                <Shield className="w-5 h-5 text-primary-600" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('home.badges.healthCertified')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-md">
                                <Star className="w-5 h-5 text-accent-500" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('home.badges.rating')}</span>
                            </div>
                        </div>
                    </motion.div>

                    {/* Enhanced Feature Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
                        {aboutFeatures.map((feature, index) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 50 }}
                                animate={inView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.6, delay: index * 0.2 }}
                                className="group"
                            >
                                <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 dark:border-gray-700 overflow-hidden">
                                    {/* Background Gradient */}
                                    <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${feature.color}`}></div>

                                    {/* Icon */}
                                    <div className={`relative w-20 h-20 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-transform duration-300`}>
                                        <feature.icon className="w-10 h-10 text-white" />
                                        <div className="absolute inset-0 bg-white/10 rounded-2xl"></div>
                                    </div>

                                    {/* Content */}
                                    <div className="text-center">
                                        <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">
                                            {feature.title}
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                            {feature.description}
                                        </p>
                                    </div>

                                    {/* Hover Effect */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-900/10 dark:to-accent-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* restaurant Bar Section */}
            <section className="relative section-padding bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
                {/* Background Effects */}
                <div className="absolute inset-0">
                    {/* Animated Gradient Orbs */}
                    <div className="absolute top-20 left-10 w-48 h-48 lg:w-64 lg:h-64 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-20 right-10 w-56 h-56 lg:w-80 lg:h-80 bg-amber-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 lg:w-96 lg:h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>

                    {/* Floating Bar Elements */}
                    <div className="absolute top-32 right-16 opacity-20">
                        <motion.div
                            animate={{ y: [-20, 20, -20], rotate: [0, 10, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className="text-amber-400"
                        >
                            <Wine className="w-12 h-12 lg:w-16 lg:h-16" />
                        </motion.div>
                    </div>
                    <div className="absolute bottom-40 left-16 opacity-20">
                        <motion.div
                            animate={{ y: [20, -20, 20], rotate: [0, -10, 0] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                            className="text-purple-400"
                        >
                            <Martini className="w-8 h-8 lg:w-12 lg:h-12" />
                        </motion.div>
                    </div>
                    <div className="absolute top-1/3 left-20 opacity-15">
                        <motion.div
                            animate={{ rotate: [0, 360] }}
                            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                            className="text-amber-300"
                        >
                            <GlassWater className="w-10 h-10 lg:w-14 lg:h-14" />
                        </motion.div>
                    </div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        {/* Bar Badge */}
                        <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500/20 to-amber-500/20 border border-purple-500/30 text-purple-300 px-6 py-3 rounded-full text-sm font-medium mb-8">
                            <Wine className="w-5 h-5" />
                            <span className="uppercase tracking-wide">{t('home.premiumBarExperience')}</span>
                            <Martini className="w-4 h-4 animate-pulse" />
                        </div>

                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 text-white leading-tight">
                            {t('restaurantBar.title')}{' '}
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-amber-400 to-purple-400">
                                {t('restaurantBar.subtitle')}
                            </span>
                        </h2>

                        <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-light mb-12">
                            {t('restaurantBar.description')}
                        </p>

                        {/* Bar Features */}
                        <div className="flex justify-center items-center space-x-8 flex-wrap gap-4 mb-12">
                            <div className="flex items-center space-x-2 bg-white/10 border border-white/20 px-6 py-3 rounded-full">
                                <ChefHat className="w-5 h-5 text-purple-400" />
                                <span className="text-sm font-medium text-white">{t('restaurantBar.features.expertBartenders')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/10 border border-white/20 px-6 py-3 rounded-full">
                                <Wine className="w-5 h-5 text-amber-400" />
                                <span className="text-sm font-medium text-white">{t('restaurantBar.features.premiumSpirits')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/10 border border-white/20 px-6 py-3 rounded-full">
                                <Music className="w-5 h-5 text-indigo-400" />
                                <span className="text-sm font-medium text-white">{t('restaurantBar.features.liveMusic')}</span>
                            </div>
                        </div>
                    </motion.div>

                    {/* Bar Features Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
                        {/* Signature Cocktails */}
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <div className="relative bg-gradient-to-br from-purple-800/40 to-purple-900/40 border border-purple-500/20 rounded-3xl p-8 hover:border-purple-400/40 transition-all duration-500 group">
                                {/* Glow Effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-amber-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                <div className="relative">
                                    {/* Cocktail Icon */}
                                    <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform duration-300">
                                        <Martini className="w-10 h-10 text-white" />
                                        <div className="absolute inset-0 bg-white/10 rounded-2xl"></div>
                                    </div>

                                    <h3 className="text-2xl font-bold text-white mb-4">{t('restaurantBar.signature.title')}</h3>
                                    <p className="text-gray-300 leading-relaxed">
                                        {t('restaurantBar.signature.description')}
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Wine Collection */}
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <div className="relative bg-gradient-to-br from-amber-800/40 to-amber-900/40 border border-amber-500/20 rounded-3xl p-8 hover:border-amber-400/40 transition-all duration-500 group">
                                {/* Glow Effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-purple-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                <div className="relative">
                                    {/* Wine Icon */}
                                    <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform duration-300">
                                        <Wine className="w-10 h-10 text-white" />
                                        <div className="absolute inset-0 bg-white/10 rounded-2xl"></div>
                                    </div>

                                    <h3 className="text-2xl font-bold text-white mb-4">{t('restaurantBar.wines.title')}</h3>
                                    <p className="text-gray-300 leading-relaxed">
                                        {t('restaurantBar.wines.description')}
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Premium Spirits */}
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <div className="relative bg-gradient-to-br from-indigo-800/40 to-indigo-900/40 border border-indigo-500/20 rounded-3xl p-8 hover:border-indigo-400/40 transition-all duration-500 group">
                                {/* Glow Effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                <div className="relative">
                                    {/* Spirits Icon */}
                                    <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform duration-300">
                                        <GlassWater className="w-10 h-10 text-white" />
                                        <div className="absolute inset-0 bg-white/10 rounded-2xl"></div>
                                    </div>

                                    <h3 className="text-2xl font-bold text-white mb-4">{t('restaurantBar.spirits.title')}</h3>
                                    <p className="text-gray-300 leading-relaxed">
                                        {t('restaurantBar.spirits.description')}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Bar Features List */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-16"
                    >
                        {[
                            { icon: ChefHat, text: t('restaurantBar.features.expertBartenders'), color: 'text-purple-400' },
                            { icon: Wine, text: t('restaurantBar.features.premiumSpirits'), color: 'text-amber-400' },
                            { icon: Martini, text: t('restaurantBar.features.customCocktails'), color: 'text-indigo-400' },
                            { icon: GlassWater, text: t('restaurantBar.features.wineCollection'), color: 'text-purple-300' },
                            { icon: Clock, text: t('restaurantBar.features.happyHour'), color: 'text-amber-300' },
                            { icon: Music, text: t('restaurantBar.features.liveMusic'), color: 'text-indigo-300' },
                        ].map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="text-center"
                            >
                                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 transition-colors duration-300">
                                    <feature.icon className={`w-8 h-8 ${feature.color} mx-auto mb-3`} />
                                    <p className="text-gray-300 text-sm font-medium leading-relaxed">{feature.text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Call to Action */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center"
                    >
                        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center max-w-md mx-auto mb-8">
                            <Link
                                to="/reservation"
                                className="group bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl shadow-lg w-full sm:w-auto"
                            >
                                <span className="text-lg">{t('restaurantBar.cta')}</span>
                            </Link>
                        </div>

                        <p className="text-gray-400 text-sm max-w-2xl mx-auto">
                            {t('restaurantBar.hours')} • {t('restaurantBar.features.expertBartenders')} • {t('restaurantBar.features.liveMusic')} • Premium Experience
                        </p>
                    </motion.div>
                </div>
            </section>



            {/* Testimonials Section */}
            <Testimonials />

            {/* Gallery Preview Section */}
            <section className="relative section-padding bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                {/* Background Decorations */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-32 left-4 w-48 h-48 lg:left-20 lg:w-64 lg:h-64 bg-primary/3 rounded-full blur-xl"></div>
                    <div className="absolute bottom-32 right-4 w-56 h-56 lg:right-20 lg:w-80 lg:h-80 bg-accent/3 rounded-full blur-xl"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 lg:w-96 lg:h-96 bg-secondary/2 rounded-full blur-xl"></div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        {/* Badge */}
                        <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary-600 px-6 py-3 rounded-full text-sm font-medium mb-8">
                            <Camera className="w-5 h-5" />
                            <span>{t('gallery.visualGallery')}</span>
                        </div>

                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 text-gray-900 dark:text-white leading-tight">
                            {t('gallery.culinaryArtistry')}
                        </h2>
                        <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed font-light mb-10">
                            {t('gallery.subtitle')}
                        </p>

                        {/* Stats */}
                        <div className="flex justify-center items-center space-x-8 flex-wrap gap-4">
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-md">
                                <ImageIcon className="w-5 h-5 text-primary-600" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('gallery.photos')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-md">
                                <Camera className="w-5 h-5 text-accent-600" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('gallery.highQuality')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-md">
                                <Award className="w-5 h-5 text-secondary-600" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('gallery.curatedCollection')}</span>
                            </div>
                        </div>
                    </motion.div>

                    {/* Gallery Grid */}
                    {galleryLoading ? (
                        <div className="text-center py-16">
                            <div className="relative w-16 h-16 mx-auto mb-6">
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                    className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full"
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Camera className="w-6 h-6 text-primary-600" />
                                </div>
                            </div>
                            <p className="text-lg text-gray-600 dark:text-gray-300 font-medium">
                                Loading gallery...
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                            {galleryItems.slice(0, 6).map((item, index) => (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="group cursor-pointer"
                                >
                                    <div className="relative overflow-hidden rounded-3xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-primary-500/20 hover:-translate-y-2">
                                        <div className="aspect-[4/3] overflow-hidden">
                                            <img
                                                src={item.image || "/placeholder.svg"}
                                                alt={item.title}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                            />
                                        </div>

                                        {/* Enhanced Overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-end justify-center p-6">
                                            <div className="text-white text-center transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500">
                                                <h3 className="text-lg font-serif font-bold mb-2">{item.title}</h3>
                                                <p className="text-xs text-white/90 leading-relaxed mb-3">{item.description}</p>

                                                {/* View Button */}
                                                <motion.div
                                                    whileHover={{ scale: 1.05 }}
                                                    className="inline-flex items-center space-x-2 bg-white/20 px-3 py-1.5 rounded-full text-xs font-medium"
                                                >
                                                    <Eye className="w-3 h-3" />
                                                    <span>View Details</span>
                                                </motion.div>
                                            </div>
                                        </div>

                                        {/* Enhanced Category Badge */}
                                        <div className="absolute top-4 left-4">
                                            <div className="flex items-center space-x-2 bg-white/90 px-3 py-2 rounded-full shadow-lg">
                                                <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                                                <span className="text-xs font-semibold text-gray-800 capitalize">
                                                    {item.gallery_category?.name || 'Gallery'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Hover Glow Effect */}
                                        <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-primary-500/10 to-accent-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    {/* CTA to Gallery Page */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center"
                    >
                        <Link
                            to="/gallery"
                            className="group inline-flex items-center space-x-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl shadow-lg"
                        >
                            <ImageIcon className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                            <span className="text-lg">{t('gallery.viewFullGallery')}</span>
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>

                        <p className="mt-4 text-gray-600 dark:text-gray-400 text-sm">
                            {t('gallery.discoverMore')}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Enhanced Contact CTA */}
            <section className="relative section-padding bg-gradient-cta overflow-hidden">
                {/* Background Effects */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full opacity-30">
                        <div className="w-full h-full bg-repeat" style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2030/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                        }}></div>
                    </div>
                    <div className="absolute top-1/4 left-4 w-48 h-48 lg:left-1/4 lg:w-64 lg:h-64 bg-white/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-1/4 right-4 w-56 h-56 lg:right-1/4 lg:w-80 lg:h-80 bg-secondary-400/20 rounded-full blur-3xl"></div>
                </div>

                <div className="container mx-auto max-w-7xl container-padding text-center relative">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <div className="inline-flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full mb-6">
                            <Phone className="w-5 h-5 text-white" />
                            <span className="text-white/90 font-medium text-sm uppercase tracking-wide">
                                {t('home.cta.readyToDine')}
                            </span>
                        </div>

                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-white mb-6 leading-tight">
                            {t('home.cta.title')}
                        </h2>

                        <p className="text-lg md:text-xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed font-light">
                            {t('home.cta.description')}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center max-w-md mx-auto">
                            <a
                                href="tel:+243971466525"
                                className="group flex items-center space-x-3 bg-white text-accent-600 hover:bg-gray-50 font-semibold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl w-full sm:w-auto justify-center border-2 border-transparent hover:border-white/20"
                            >
                                <Phone className="w-6 h-6 group-hover:animate-pulse" />
                                <span className="text-lg">{t('home.cta.callNow')}</span>
                            </a>
                            <Link
                                to="/reservation"
                                className="group bg-transparent border-2 border-white text-white hover:bg-white hover:text-accent-600 font-semibold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl w-full sm:w-auto"
                            >
                                <span className="text-lg">{t('hero.bookNow')}</span>
                            </Link>
                        </div>

                        {/* Additional info */}
                        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                            <div className="bg-white/10 rounded-xl p-6 border border-white/20">
                                <Clock className="w-8 h-8 text-white mx-auto mb-3" />
                                <h4 className="text-white font-semibold mb-2">{t('home.features.extendedHours.title')}</h4>
                                <p className="text-white/80 text-sm">{t('home.features.extendedHours.description')}</p>
                            </div>
                            <div className="bg-white/10 rounded-xl p-6 border border-white/20">
                                <MapPin className="w-8 h-8 text-white mx-auto mb-3" />
                                <h4 className="text-white font-semibold mb-2">{t('home.features.primeLocation.title')}</h4>
                                <p className="text-white/80 text-sm">{t('home.features.primeLocation.description')}</p>
                            </div>
                            <div className="bg-white/10 rounded-xl p-6 border border-white/20">
                                <Star className="w-8 h-8 text-white mx-auto mb-3" />
                                <h4 className="text-white font-semibold mb-2">{t('home.features.allYouCanEat.title')}</h4>
                                <p className="text-white/80 text-sm">{t('home.features.allYouCanEat.description')}</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Home;
